import {isFeatureEnabled} from '@github-ui/feature-flags'
import {repoAttestationsIndexPath} from '@github-ui/paths'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import {useLocation} from '@github-ui/react-router'
import {useClientValue} from '@github-ui/use-client-value'
import {useNavigate} from '@github-ui/use-navigate'
import {Heading, Link, PageLayout} from '@primer/react'
import {Pagehead} from '@primer/react/deprecated'

import {AttestationsList} from '../components/AttestationsList'
import type {AttestationsPayload, SortOrderType} from '../types'
import {SortOrder} from '../types'
import styles from './AttestationsIndex.module.css'

export const AttestationsIndex = () => {
  const payload = useRoutePayload<AttestationsPayload>()
  const {repo, attestations, pageInfo, totalCount, hasWritePermission} = payload

  const navigate = useNavigate()
  const location = useLocation()

  const [state] = useClientValue(
    () => {
      const params = new URLSearchParams(location.search)

      let currentPage = 1
      let pageCount = 1

      // Note: The attestations API only provides information about the "next" page when paginating forwards
      // and the "previous" page when paginating backwards. Therefore, we assume:
      // - There is always a next page when paginating backwards (via "previous").
      // - There is always a previous page when paginating forwards (via "next").

      // If there is a next page or we are paginating "backwards" (via "previous"), increment the page count.
      // This ensures the "next" button is displayed, and the current page remains 1.
      if (pageInfo?.hasNextPage || params.get('before')) {
        pageCount += 1
      }

      // If there is a previous page or we are paginating "forwards" (via "next"), increment the page count
      // and set the current page to 2. This ensures the "previous" button is displayed.
      if (pageInfo?.hasPreviousPage || params.get('after')) {
        pageCount += 1
        currentPage = 2
      }

      return {
        sort: params.get('direction') === '1' ? SortOrder.Oldest : SortOrder.Newest,
        pagination: {
          currentPage,
          pageCount,
        },
      }
    },
    {
      sort: SortOrder.Newest,
      pagination: {currentPage: 1, pageCount: 1},
    },
    [location.search, pageInfo],
  )

  const showSortDropdown = isFeatureEnabled('attestations_sorting')

  const handleSortChange = (sortOrder: SortOrderType) => {
    const params = new URLSearchParams(location.search)
    params.delete('after')
    params.delete('before')
    params.set('direction', sortOrder === SortOrder.Oldest ? '1' : '2')

    const url = repoAttestationsIndexPath({
      repo,
      query: params.toString(),
    })

    navigate(url)
  }

  const handlePageChange = (pageIndex: number) => {
    if (!pageInfo) return

    const pagination: {before?: string; after?: string} = {}

    if (pageIndex > state.pagination.currentPage) {
      pagination.after = pageInfo.endCursor
    } else if (pageIndex < state.pagination.currentPage) {
      pagination.before = pageInfo.startCursor
    }

    const sortOrder = state.sort
    const url = repoAttestationsIndexPath({
      repo,
      pagination,
      query: showSortDropdown ? `direction=${sortOrder}` : '',
    })

    navigate(url)
  }

  return (
    <PageLayout rowGap="none">
      <PageLayout.Header>
        <Pagehead className={styles.Pagehead}>
          <div className={styles.Box}>
            <div className={styles.Box_1}>
              <Heading as="h2" className={styles.Heading}>
                Attestations
              </Heading>
            </div>
            {payload.feedbackUrl?.length > 0 && (
              <div>
                <Link href={payload.feedbackUrl} target="_blank" className="f6">
                  Give feedback
                </Link>
              </div>
            )}
          </div>
        </Pagehead>
      </PageLayout.Header>
      <PageLayout.Content as="div">
        <AttestationsList
          repo={repo}
          attestations={attestations}
          totalCount={totalCount}
          onSortChange={handleSortChange}
          onPageChange={handlePageChange}
          currentSort={state.sort}
          pageCount={state.pagination.pageCount}
          currentPage={state.pagination.currentPage}
          hasWritePermission={hasWritePermission}
        />
      </PageLayout.Content>
    </PageLayout>
  )
}

try{ AttestationsIndex.displayName ||= 'AttestationsIndex' } catch {}